// attacks_ignore_natural_damage_mitigation (2026-10-07, Psion Metamorph L8 menta "Living Arsenal": "your attacks ignore Damage
// Mitigation from natural armor, hide, shell, bark, or flesh-based defenses"). An ATTACK-roll hit skips the part of the target's
// Damage Mitigation the creature has BY NATURE; worn gear and active effects (spells, mentas, abilities, statuses) still apply.
//
// NATURAL Damage Mitigation = the compiled damage_mitigation of the target's own base creature DEFINITION (its statblock: the creature
// row plus its creature type / sub-type / ancestry / lineage carriers — the blob options/api/object_inc.php serves, which is what the
// page's object cache holds), clamped to [0, current DM]. A player character has no statblock share; an object is not a creature.
//     subtracted = DM - min(DM, natural)      (then the typed exemption of ignore_damage_mitigation_damage_type reduces from that)
// Nothing left to subtract logs "Damage Mitigation Ignored".
// Providers (read at damage time, no creature-root fold — the damageIgnoresAllMitigation() walk): the dealt item / its attack ability,
// the dealer's row, an ACTIVE non-aura passive entry (a combat-start timed one only while its status is held), a held status's
// definition (Living Arsenal is a Self menta held as its own status), or a feat whose ability conditions are met.
// Lanes: damage-type-roll.ts (the ability lane's DM block) and the natural-weapon lane (weapon-attack.ts plans it, weapon-attack-extra-
// damage.ts totalDamage applies it). The legacy PHP copy is ft_attacks_ignore_natural_damage_mitigation.php — retirement row R-180.
import { type JanusCtx, type Row, itemField, record, upper } from '../context.js';
import { ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION, ctxDamageProviders, damageProviderDefinitions, damageProviderFlag, type DamageProviderReaders } from './ignore-damage-mitigation-damage-type.js';
export { ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION } from './ignore-damage-mitigation-damage-type.js';

/** The two spell attack roll types attack-roll.ts resolves (a weapon template hit is the other attack fragment). */
export const SPELL_ATTACK_ROLL_TYPES: ReadonlySet<string> = new Set(['5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C']);
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** The Damage Mitigation still subtracted once `natural` of it is ignored (natural already clamped to [0, mitigation]). */
export function mitigationAfterNaturalIgnore(mitigation: number, natural: number): number {
  if (!(natural > 0)) return mitigation;
  return Math.max(0, mitigation - Math.min(mitigation, natural));
}

/** PHP (int) cast of a layer_value() result ('+5' → 5, 5 → 5, null → 0). */
const phpIntCast = (v: unknown): number => {
  const x = Array.isArray(v) ? v[0] : v;
  if (typeof x === 'number') return Number.isFinite(x) ? Math.trunc(x) : 0;
  const n = Number.parseInt(String(x ?? '').trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

/** ft_natural_damage_mitigation()'s gate: the base creature id of a CREATURE target, '' when it has no statblock share. */
export function naturalMitigationBaseId(target: Row): string {
  const type = String(itemField(target, 'item_type') ?? record(target.encounter_creatures)?.item_type ?? '');
  if (type !== 'creature') return '';
  const id = upper(itemField(target, 'item_id'));
  return UUID.test(id) ? id : '';
}

/** ft_natural_damage_mitigation(): the base definition's compiled damage_mitigation clamped to [0, current]; null = the definition is
 *  not held (the caller hands back / prefetches it). */
export function naturalDamageMitigation(target: Row, current: number, definition: (id: string) => Row | null,
  value: (row: Row, key: string) => unknown): number | null {
  if (!(current > 0)) return 0;
  const id = naturalMitigationBaseId(target);
  if (!id) return 0;
  const def = definition(id);
  if (!def) return null;
  return Math.max(0, Math.min(current, phpIntCast(value(def, 'damage_mitigation'))));
}

/** Whether a definition / row carries the flag (layer value or a finalize-style root slot). */
export function carriesNaturalMitigationIgnore(row: Row | null | undefined, value: (row: Row, key: string) => unknown): boolean {
  return !!row && damageProviderFlag({ value }, [row], ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION);
}

/** Existing natural-ignore callers share the damage provider reader contract. */
export interface NaturalMitigationReaders extends DamageProviderReaders {}

/** Natural-ignore wrappers retained for the weapon prefetch and callers that ask only this flag. */
export function readersIgnoreNaturalMitigation(read: NaturalMitigationReaders, attacker: Row, weapon: Row | null): boolean | string {
  const providers = damageProviderDefinitions(read, attacker, weapon, new Set([ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION]));
  return typeof providers === 'string' ? providers : damageProviderFlag(read, providers, ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION);
}
export function attacksIgnoreNaturalMitigation(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  return damageProviderFlag(ctx.read, ctxDamageProviders(ctx, caster, ability, new Set([ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION])), ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION);
}
